import { inject, Injectable } from '@angular/core';

import {
  ChapterWidget,
  ComponentListWidget,
  ComponentWidget,
  FlowchartActionDto,
  FlowchartDecisionNodeDto,
  FlowchartElementType,
  FlowchartNodeDto,
  FlowchartNodeType,
  FlowchartStartNodeDto,
  FlowchartWidget,
  Guideline,
  GuidelineLinkDto,
  GuidelineWidget,
  LinkElementType,
} from '@treetop/shared/model';

import { GuidelineService } from './guideline-page-domain/guideline.service';
import { getWidgetsChildren } from './guideline-page-domain/util/guideline.service.util';
import { GuidelineViewService } from './guideline-view.service';
import { ScrollService } from './scroll.service';
import { flattenFlowchartElements } from '../../../../model/src/lib/widgets/flowchart.model';
import { RxStore } from '../application/rx-store';

@Injectable()
export class LinksService {
  private guidelineService = inject(GuidelineService);
  private scrollService = inject(ScrollService);
  private guidelineViewService = inject(GuidelineViewService, { optional: true });

  public showInternalLinks = new RxStore<boolean>(true);
  public links = new RxStore<GuidelineLinkDto[]>([]); // USED!

  public getAllElementsLinkingToThisItem(widgetOrFlowchartElement: GuidelineWidget | FlowchartNodeType): string[] {
    const linkedElementIds: string[] = [];

    const links = 'links' in widgetOrFlowchartElement ? widgetOrFlowchartElement.links : [];
    const widgetMap = this.guidelineService.map();

    Object.values(widgetMap).forEach((widgetSignal) => {
      const widget = widgetSignal();

      if (links?.some((link) => link.toElementUniqueId === widget.uniqueId || link.toChapterUniqueId === widget.uniqueId)) {
        linkedElementIds.push(widget.uniqueId);
      }

      if (widget.widgetType === 'flowchart') {
        const flowchartElements = flattenFlowchartElements(widget);
        flowchartElements.forEach((element) => {
          if (links?.some((link) => link.toElementUniqueId === element.uniqueId || link.toChapterUniqueId === element.uniqueId)) {
            linkedElementIds.push(element.uniqueId);
          }

          if (this.checkFlowchartElementDescriptionsForElementId(widget.elements, element.uniqueId)) {
            linkedElementIds.push(element.uniqueId);
          }
        });
      }
    });

    return linkedElementIds;
  }

  private checkFlowchartElementDescriptionsForElementId(elements: FlowchartNodeType[], elementId: string): boolean {
    for (const element of elements) {
      if ('guidelineAction' in element && element.guidelineAction?.description?.includes(`data-element-id="${elementId}"`)) {
        return true;
      }

      if ('guidelineCriteria' in element && element.guidelineCriteria?.description?.includes(`data-element-id="${elementId}"`)) {
        return true;
      }

      if ('guidelineObservation' in element && element.guidelineObservation?.description?.includes(`data-element-id="${elementId}"`)) {
        return true;
      }

      if (element.children?.length > 0) {
        if (this.checkFlowchartElementDescriptionsForElementId(element.children, elementId)) {
          return true;
        }
      }
    }

    return false;
  }

  public openExternalGuideline(guidelineSlug: string, isPublicPage: boolean, isPreviewMode: boolean, isUpdateReviewMode: boolean) {
    const host = window.location.origin;
    let path: string;
    if (isPublicPage) {
      path = `/public/${guidelineSlug}`;
    } else if (isPreviewMode || isUpdateReviewMode) {
      path = `/guideline/preview/${guidelineSlug}`;
    } else {
      path = `/guideline/${guidelineSlug}`;
    }
    window.open(`${host}${path}`, '_blank');
  }

  public openAnchorLinkFromTextEditor(htmlElement: HTMLElement) {
    const toElementId = htmlElement.getAttribute('data-element-id');
    const toElementType = htmlElement.getAttribute('data-element-type');

    switch (Number(toElementType)) {
      case LinkElementType.Guideline:
        this.openExternalGuideline(
          toElementId,
          this.guidelineViewService?.isPublicPage(),
          this.guidelineViewService?.isPreviewMode(),
          this.guidelineViewService?.isPublishReviewMode(),
        );
        break;
      case LinkElementType.Widget:
        this.scrollService.scrollTo(`${toElementId}`, true, true);
        break;
      case LinkElementType.FlowchartNode:
        this.scrollService.scrollToFlowchartElement(toElementId);
        break;
    }
  }

  public searchForValueInGuideline(guideline: Guideline, value: string): InternalLinkSearchResult[] {
    const chaptersInOrder = getWidgetsChildren<ChapterWidget>(
      guideline.chaptersWidgetIds?.map((id) => guideline.widgetMap[id] as ChapterWidget) ?? [],
      guideline.widgetMap,
    );

    return chaptersInOrder.flatMap((chapter) =>
      this.searchChapter(chapter, value, guideline.widgetMap).map((result) => {
        return {
          ...result,
          path: this.guidelineService.numberedChapters()[chapter.uniqueId]
            ? `${this.guidelineService.numberedChapters()[chapter.uniqueId]}. ${chapter.title}`
            : chapter.title,
        };
      }),
    );
  }

  private searchChapter(chapter: ChapterWidget, value: string, widgetMap: Record<string, GuidelineWidget>): InternalLinkSearchResult[] {
    const widgets = chapter.widgetIds?.map((id) => widgetMap[id]) ?? [];

    const searchResults: InternalLinkSearchResult[] = [];

    if (chapter.title?.toLowerCase().includes(value.toLowerCase())) {
      searchResults.push({
        uniqueId: chapter.uniqueId,
        title: chapter.title,
        toElementType: LinkElementType.Widget,
        toChapterUniqueId: chapter.uniqueId,
      });
    }

    widgets.forEach((widget) => {
      switch (widget.widgetType) {
        case 'component-list': {
          searchResults.push(...this.searchComponentListWidget(value, widget, widgetMap));
          break;
        }
        case 'flowchart': {
          searchResults.push(...this.searchFlowchartWidget(value, widget));
        }
      }
    });

    return searchResults;
  }

  private searchComponentListWidget(
    value: string,
    componentListWidget: ComponentListWidget,
    widgetMap: Record<string, GuidelineWidget>,
  ): InternalLinkSearchResult[] {
    const components =
      getWidgetsChildren<ComponentWidget>(componentListWidget.widgetIds?.map((id) => widgetMap[id] as ComponentWidget) ?? [], widgetMap) ??
      [];

    return components.map((componentWidget) => this.searchComponent(value, componentWidget)).filter(Boolean) as InternalLinkSearchResult[];
  }

  private searchComponent(value: string, componentWidget: ComponentWidget): InternalLinkSearchResult {
    const component = componentWidget.component;

    if (component.title?.toLowerCase().includes(value.toLowerCase())) {
      return {
        uniqueId: componentWidget.uniqueId,
        title: component.title,
        toElementType: LinkElementType.Widget,
      };
    }
  }

  private searchFlowchartWidget(value: string, widget: FlowchartWidget): InternalLinkSearchResult[] {
    const flowchartElements = flattenFlowchartElements(widget);

    const searchResults: InternalLinkSearchResult[] = [];

    flowchartElements.forEach((element) => {
      const title = this.flowchartTitleResolver(element);
      if (title?.toLowerCase().includes(value.toLowerCase())) {
        searchResults.push({
          uniqueId: element.uniqueId,
          title: title,
          toElementType: LinkElementType.FlowchartNode,
        });
      }
    });

    return searchResults;
  }

  private flowchartTitleResolver(element: FlowchartNodeDto): string | undefined {
    switch (element.elementType) {
      case FlowchartElementType.FlowchartAction: {
        const action = element as FlowchartActionDto;
        return action.guidelineAction?.title;
      }

      case FlowchartElementType.FlowchartDecisionNode: {
        const decision = element as FlowchartDecisionNodeDto;
        return decision.guidelineCriteria?.title ?? decision.guidelineObservation?.title;
      }

      case FlowchartElementType.FlowchartStartNode: {
        const start = element as FlowchartStartNodeDto;
        return start.guidelineCriteria?.title ?? start.guidelineAction?.title;
      }

      default:
        return undefined;
    }
  }
}

export interface InternalLinkSearchResult {
  uniqueId: string;
  title: string;
  toElementType: LinkElementType;
  path?: string;
  toChapterUniqueId?: string;
  externalGuidelineUniqueId?: string;
}
